import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetMonitoredObject, GetCurrent } from "../Api/getData";
import { useAuth } from "../context/AuthContext";

export const AuthData = () => {
    const { user } = useAuth();
    return user;
}

export const useDataFilter = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        const dataFilter = localStorage.getItem('Filter');
        if (dataFilter) {
            const parsedData = JSON.parse(dataFilter);
            setData(parsedData);
        }
    }, []);

    // Listen for storage changes
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'Filter') {
                const dataFilter = localStorage.getItem('Filter');
                if (dataFilter) {
                    const parsedData = JSON.parse(dataFilter);
                    setData(parsedData);
                } else {
                    setData(null);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Also listen for custom events (for same-tab updates)
        const handleCustomStorageChange = () => {
            const dataFilter = localStorage.getItem('Filter');
            if (dataFilter) {
                const parsedData = JSON.parse(dataFilter);
                setData(parsedData);
            } else {
                setData(null);
            }
        };

        window.addEventListener('filterUpdated', handleCustomStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('filterUpdated', handleCustomStorageChange);
        };
    }, []);

    return data;
}

export const useFilterDetails = () => {
    const [filterDetails, setFilterDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const filterData = useDataFilter();

    useEffect(() => {
        const fetchFilterDetails = async () => {
            if (filterData && filterData.KodeCabang && filterData.ShipCode) {
                setIsLoading(true);
                try {
                    const response = await GetMonitoredObject({
                        AccountId: filterData.KodeCabang || 'SGM',
                        ObjectCode: filterData.ShipCode || 'G2008',
                    });
                    const dataCabang = response.Cabang.find(
                        (item) => item.KodeCabang === filterData.KodeCabang
                    );
                    const dataShip = response.Ship.find(
                        (item) => item.ShipCode === filterData.ShipCode
                    );

                    if (dataCabang && dataShip) {
                        const details = {
                            ...dataCabang,
                            ...dataShip,
                        };
                        setFilterDetails(details);
                    }
                } catch (error) {
                    console.log('Error fetching filter details:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchFilterDetails();
    }, [filterData]);

    // Listen for filter changes to refresh details immediately
    useEffect(() => {
        const handleFilterChange = () => {
            const dataFilter = localStorage.getItem('Filter');
            if (dataFilter) {
                const parsedData = JSON.parse(dataFilter);
                if (parsedData.KodeCabang && parsedData.ShipCode) {
                    const fetchFilterDetails = async () => {
                        setIsLoading(true);
                        try {
                            const response = await GetMonitoredObject('SGM');
                            const dataCabang = response.Cabang.find(
                                (item) => item.KodeCabang === parsedData.KodeCabang
                            );
                            const dataShip = response.Ship.find(
                                (item) => item.ShipCode === parsedData.ShipCode
                            );

                            if (dataCabang && dataShip) {
                                const details = {
                                    ...dataCabang,
                                    ...dataShip,
                                };
                                setFilterDetails(details);
                            }
                        } catch (error) {
                            console.log('Error fetching filter details:', error);
                        } finally {
                            setIsLoading(false);
                        }
                    };
                    fetchFilterDetails();
                }
            }
        };

        window.addEventListener('filterUpdated', handleFilterChange);

        return () => {
            window.removeEventListener('filterUpdated', handleFilterChange);
        };
    }, []);

    return { filterDetails, isLoading };
}

export const useSensorData = () => {
    const [sensorData, setSensorData] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const filterData = useDataFilter();

    useEffect(() => {
        const fetchSensorData = async () => {
            if (filterData && filterData.KodeCabang && filterData.ShipCode) {
                try {
                    const response = await GetCurrent({
                        AccountId: filterData.KodeCabang || 'SGM',
                        ObjectCode: filterData.ShipCode || 'G2008',
                    });
                    setSensorData(response);

                    // Extract last update date
                    if (response && response.length > 0) {
                        const lastDate = response[1]?.RefDate || response[0]?.RefDate;
                        setLastUpdate(lastDate);
                    }
                } catch (error) {
                    console.log('Error fetching sensor data:', error);
                }
            }
        };

        fetchSensorData();

        // Set interval to refresh data every 30 minutes
        const intervalId = setInterval(fetchSensorData, 1800000);

        return () => clearInterval(intervalId);
    }, [filterData]);

    // Listen for filter changes to refresh data immediately
    useEffect(() => {
        const handleFilterChange = () => {
            const dataFilter = localStorage.getItem('Filter');
            if (dataFilter) {
                const parsedData = JSON.parse(dataFilter);
                if (parsedData.KodeCabang && parsedData.ShipCode) {
                    // Refresh sensor data when filter changes
                    const fetchSensorData = async () => {
                        try {
                            const response = await GetCurrent(
                                {
                                    AccountId: parsedData.KodeCabang || 'SGM',
                                    ObjectCode: parsedData.ShipCode || 'G2008',
                                }
                            );
                            setSensorData(response);

                            // Extract last update date
                            if (response && response.length > 0) {
                                const lastDate = response[1]?.RefDate || response[0]?.RefDate;
                                setLastUpdate(lastDate);
                            }
                        } catch (error) {
                            console.log('Error fetching sensor data:', error);
                        }
                    };
                    fetchSensorData();
                }
            }
        };

        window.addEventListener('filterUpdated', handleFilterChange);

        return () => {
            window.removeEventListener('filterUpdated', handleFilterChange);
        };
    }, []);

    return { sensorData, lastUpdate };
}

export const ValueFilter = ({ KodeCabang, ShipCode }) => {
    const [filter, setFilter] = useState(null);

    useEffect(() => {
        const fetchDataObject = async () => {
            try {
                const response = await GetMonitoredObject({
                    AccountId: KodeCabang || 'SGM',
                    ObjectCode: ShipCode || 'G2008',
                });
                const dataCabang = response.Cabang.find(
                    (item) => item.KodeCabang === KodeCabang
                );
                const dataShip = response.Ship.find(
                    (item) => item.ShipCode === ShipCode
                );

                // Combine the data if both are found
                if (dataCabang && dataShip) {
                    const data = {
                        ...dataCabang,
                        ...dataShip,
                    };
                    setFilter(data);
                } else {
                    console.log('No matching data found for the provided KodeCabang or ShipCode.');
                    setFilter(null); // Set filter to null if no data is found
                }
            } catch (error) {
                console.log(error);
            }
        };

        if (KodeCabang && ShipCode) {
            fetchDataObject();
        }
    }, [KodeCabang, ShipCode]);

    return (
        <div className="flex flex-col lg:flex-row lg:gap-2 text-xl">
            {filter ? ( // Check if filter is not null
                <>
                    <p className="text-[16px] lg:text-lg">{`Branch Name : ${filter.NamaCabang}`}</p>
                    <p className="hidden lg:block">||</p>
                    <p className="text-[16px] lg:text-lg">{`Ship Name : ${filter.ShipName}`}</p>
                </>
            ) : (
                <p></p>
            )}
        </div>
    );
};